import { CharacterCount } from '@tiptap/extension-character-count';
import { Mention } from '@tiptap/extension-mention';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { PageData } from 'types/Page';
import { Command, CommandProps, Extension, ReactRenderer } from '@tiptap/react';
import tippy, { GetReferenceClientRect, Instance } from 'tippy.js';
import { Plugin, PluginKey } from 'prosemirror-state';
import { HardBreak } from '@tiptap/extension-hard-break';
import { MentionableUser, User, UserRole } from 'types/User';
import StringUtils from 'utils/string';
import { TextEditorFeature } from 'app/modules/textEditor/constants/textEditorFeature';
import type { SuggestionOptions } from '@tiptap/suggestion';
import twitterText from 'twitter-text';
import { UseTextEditorProps } from 'app/modules/textEditor/hooks/useTextEditor';
import Placeholder from '@tiptap/extension-placeholder';
import Document from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import ArrayUtils from 'app/utils/array';
import MentionSuggestions from 'app/modules/textEditor/components/MentionSuggestions';
import { SuggestionsList } from 'app/modules/textEditor/components/SuggestionsList';
import { FileAttachment } from '../hooks/useFileAttachments';
import { EditorView } from '@tiptap/pm/view';
import { Fragment, Node, Slice } from '@tiptap/pm/model';
import { stripMentionMarkup } from 'app/modules/textEditor/utils/mentionCleaner';
import { isMentionQueryWithinBound } from 'app/modules/textEditor/utils/mentionQueryBound';
import {
  DIRECT_MENTION_PAGE_TYPES,
  PAGE_MENTION_PAGE_TYPES,
} from 'app/modules/textEditor/constants/mentionNetworks';

export { PAGE_MENTION_PAGE_TYPES, DIRECT_MENTION_PAGE_TYPES };

declare module '@tiptap/extension-character-count' {
  export interface CharacterCountStorage {
    specials(): {
      mentions: number;
      hashtags: number;
    };
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customExtension: {
      insertUniqueUserMention: (
        user: Pick<User, 'id' | 'name' | 'surname' | 'role'>,
        options?: {
          position?: number;
        },
      ) => ReturnType;
      removeMentions: (userIds: number[]) => ReturnType;
      insertEmailMention: (email: string, role: UserRole) => ReturnType;
      removeEmailMentions: (emails: string[]) => ReturnType;
    };
  }
}

/** The suggestion trigger. Sliced off the matched span to recover the query. */
export const MENTION_CHAR = '@';

type Props = Pick<
  UseTextEditorProps,
  'valueType' | 'placeholder' | 'features' | 'page' | 'mentionableUsers'
> & {
  setRefineTextSelectionVisible(value: boolean): void;
};

export function getTextEditorExtensions({
  valueType,
  placeholder,
  features,
  page,
  mentionableUsers,
  setRefineTextSelectionVisible,
}: Props) {
  // order matters
  const extensions = [];

  if (valueType === 'raw') {
    extensions.push(PlainTextSeparatedWithHardBreaks);
  }

  extensions.push(
    Placeholder.configure({ placeholder }),
    Document,
    Paragraph,
    Text,
    Link.configure({ openOnClick: false }),
  );

  if (features.includes(TextEditorFeature.UndoRedo)) {
    extensions.push(History);
  }

  if (features.includes(TextEditorFeature.CharacterCounter)) {
    extensions.push(ExtendedCharacterCounter);
  }

  if (page) {
    if (
      features.includes(TextEditorFeature.PageMention) &&
      PAGE_MENTION_PAGE_TYPES.includes(page.type)
    ) {
      extensions.push(PageMention(page));
    }
  } else if (
    mentionableUsers &&
    features.includes(TextEditorFeature.UserMention)
  ) {
    extensions.push(UserMention());
  }

  if (features.includes(TextEditorFeature.FileAttachments)) {
    extensions.push(FileAttachmentsExtensions());
  }

  if (features.includes(TextEditorFeature.AIContent)) {
    extensions.push(
      AIContentExtension({
        setRefineTextSelectionVisible,
      }),
    );
  }

  if (features.includes(TextEditorFeature.ParseLink)) {
    extensions.push(ClickableLinksExtension);
  }

  extensions.push(MentionCleanupExtension);

  return extensions;
}

const ExtendedCharacterCounter = CharacterCount.extend({
  onBeforeCreate() {
    /// must be inspired by original at https://github.com/ueberdosis/tiptap/blob/main/packages/extension-character-count/src/character-count.ts#L50
    this.storage.characters = (options) => {
      const node = options?.node || this.editor.state.doc;
      const mode = options?.mode || this.options.mode;

      if (mode === 'textSize') {
        const text = node.textBetween(0, node.content.size, undefined, ' ');

        if (this.editor.storage.props?.page?.type === TYPE.TWITTER) {
          return twitterText.getTweetLength(text);
        }

        return text.length;
      }

      return node.nodeSize;
    };

    this.storage.words = (options) => {
      const node = options?.node || this.editor.state.doc;
      const text = node.textBetween(0, node.content.size, ' ', ' ');
      const words = text.split(' ').filter((word) => word !== '');

      return words.length;
    };

    // added functionality
    this.storage.specials = () => {
      const text = this.editor.getText();

      return {
        hashtags: text.split('#').length - 1,
        mentions: text.split('@').length - 1,
      };
    };
  },
});

interface MentionTextAttrs {
  id: string;
  label: string;
  role?: string;
  link?: string | null;
}

export function renderMentionText({
  pageType,
  attrs,
}: {
  pageType: number;
  attrs: MentionTextAttrs;
}): string {
  switch (pageType) {
    case TYPE.FACEBOOK:
      return `@[${attrs.label}](page:${attrs.id})`;
    case TYPE.LINKEDIN:
      // The `+<profile url>` suffix is not new syntax: the shared mention
      // parser already accepts and discards it
      // (`…(urn:li:person:<id>)(?:\+[^)]*)?`), `textEditorConvertor` already
      // reads it back off `urn:li:person:`, and the post preview's LinkedIn
      // branch already looks for a URL in the token BEFORE it looks for a
      // company id. Every consumer was ready for it; nothing was writing it.
      //
      // Without it a person mention carries only an opaque member id, and the
      // preview — whose only other branches are `company:` and
      // `urn:organization:` — fell through to an empty id and rendered
      // `https://www.linkedin.com/company/`, dead on every person mention.
      // Omitted when there is no URL to add, which keeps the stored shape
      // byte-identical to before for anything that has no vanity name.
      return attrs.role === 'user'
        ? `@[${attrs.label}](urn:li:person:${attrs.id}${
            attrs.link ? `+${attrs.link}` : ''
          })`
        : `@[${attrs.label}](company:${attrs.id})`;
    default:
      return `@${attrs.label}`;
  }
}

const PageMention = (page: Pick<PageData, 'type' | 'id'>) => {
  return Mention.extend({
    renderText({ node }) {
      return renderMentionText({
        pageType: page.type,
        attrs: node.attrs as MentionTextAttrs,
      });
    },
    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => {
            if (!attributes.id) return {};
            return { 'data-id': attributes.id };
          },
        },
        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => {
            if (!attributes.label) return {};
            return { 'data-label': attributes.label };
          },
        },
        role: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-role'),
          renderHTML: (attributes) => {
            if (!attributes.role) return {};
            return { 'data-role': attributes.role };
          },
        },
        link: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-link'),
          renderHTML: (attributes) => {
            if (!attributes.link) return {};
            return { 'data-link': attributes.link };
          },
        },
      };
    },
  }).configure({
    HTMLAttributes: { class: 'tw-bg-primary-10' },
    suggestion: {
      // Multi-word page names — "Coca Cola", "Red Bull" — could not be searched
      // at all before this: the plugin's default matcher is `@[^\s@]*`, so the
      // first space ended the match and closed the popup. The `_`-as-a-space
      // workaround still works (`useMentionSuggestions` substitutes it), but
      // nobody should have to know that.
      //
      // `allowSpaces` alone is the thing the old TODO here warned about: its
      // matcher runs from the `@` to the cursor with no word limit, so the
      // popup would follow the user across the rest of the line. `allow` is
      // what bounds it — see `isMentionQueryWithinBound`.
      allowSpaces: true,
      allow: ({ state, range }) =>
        isMentionQueryWithinBound(
          state.doc
            .textBetween(range.from, range.to)
            .slice(MENTION_CHAR.length),
        ),
      render: () =>
        suggestionsRenderer({
          pageId: page.id,
          pageType: page.type,
        }),
    },
  });
};

const removeMentionNodes = (
  { state, dispatch }: CommandProps,
  matches: (node: Node) => boolean,
): boolean => {
  if (!dispatch) {
    return false;
  }

  const mentionType = state.schema.nodes.mention;
  const positions: { from: number; to: number }[] = [];

  state.doc.descendants((node, pos) => {
    if (node.type === mentionType && matches(node)) {
      const endPosition = pos + node.nodeSize;
      const isNextCharacterSpace =
        state.doc.textBetween(endPosition, endPosition + 1, undefined, '\0') ===
        ' ';
      positions.push({
        from: pos,
        to: isNextCharacterSpace ? endPosition + 1 : endPosition,
      });
    }
  });

  if (positions.length === 0) {
    return false;
  }

  let transaction = state.tr;
  positions.reverse().forEach(({ from, to }) => {
    transaction = transaction.delete(from, to);
  });

  dispatch(transaction);
  return true;
};

const UserMention = () => {
  return Mention.extend({
    addCommands() {
      return {
        insertUniqueUserMention:
          (
            user: Pick<User, 'id' | 'name' | 'surname' | 'role'>,
            options?: { position?: number },
          ): Command =>
          ({ chain, editor }: CommandProps) => {
            const editorContent = editor.state.doc;
            let alreadyExists = false;

            const getUserFullName = (): string => {
              const { name, surname } = user;
              return surname ? `${name} ${surname}` : name;
            };

            editorContent.descendants((node) => {
              if (
                node.type.name === this.name &&
                node.attrs.id === user.id &&
                node.attrs.label === getUserFullName()
              ) {
                alreadyExists = true;
              }
            });

            if (alreadyExists) {
              return true;
            }

            const currentCursorPosition = editor.state.selection.head;
            const position = options?.position ?? currentCursorPosition;

            const mentionNode = {
              type: this.name,
              attrs: {
                id: user.id,
                label: getUserFullName(),
                role: user.role,
              },
            };
            const textNode = { type: 'text', text: ' ' };

            return chain()
              .insertContentAt(position, [mentionNode, textNode])
              .run();
          },

        removeMentions:
          (userIds: number[]): Command =>
          (props: CommandProps) =>
            removeMentionNodes(props, (node) =>
              userIds.includes(node.attrs.id),
            ),

        insertEmailMention:
          (email: string, role: UserRole): Command =>
          ({ chain, editor }: CommandProps) => {
            let alreadyExists = false;

            editor.state.doc.descendants((node) => {
              if (node.type.name === this.name && node.attrs.email === email) {
                alreadyExists = true;
              }
            });

            if (alreadyExists) {
              return true;
            }

            const position = editor.state.selection.head;
            const mentionNode = {
              type: this.name,
              attrs: { id: null, label: email, email, role },
            };
            const textNode = { type: 'text', text: ' ' };

            return chain()
              .insertContentAt(position, [mentionNode, textNode])
              .run();
          },

        removeEmailMentions:
          (emails: string[]): Command =>
          (props: CommandProps) =>
            removeMentionNodes(props, (node) =>
              emails.includes(node.attrs.email),
            ),
      };
    },
    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => {
            if (!attributes.id) return {};

            return { 'data-id': attributes.id };
          },
        },

        email: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-email'),
          renderHTML: (attributes) => {
            if (!attributes.email) return {};

            return { 'data-email': attributes.email };
          },
        },

        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => {
            if (!attributes.label) return {};

            return { 'data-label': attributes.label };
          },
        },

        role: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-role'),
          renderHTML: (attributes) => {
            if (!attributes.role) return {};

            return { 'data-role': attributes.role };
          },
        },
      };
    },

    renderText({ node }) {
      if (!node.attrs.id) {
        return '';
      }

      return `@[${node.attrs.id}]`;
    },
  }).configure({
    HTMLAttributes: {
      class:
        "mention tw-text-primary-100 tw-bg-primary-10 data-[role='client']:!tw-text-purple-100 data-[role='client']:!tw-bg-purple-10 tw-rounded-sm tw-px-1",
    },
    suggestion: {
      // TODO allow space bugfix https://github.com/ueberdosis/tiptap/issues/214#issuecomment-964557956
      // allowSpaces: true,
      items: async ({ query, editor }) => {
        const mentionableUsers = editor.storage.props
          .mentionableUsers as MentionableUser[];

        query = query.replaceAll('_', ' ');

        return ArrayUtils.sortByKey(
          mentionableUsers
            .map((user) => ({
              ...user,
              image: user.avatar?.src,
              name: `${user.name} ${user.last_name ?? ''}`,
              role: user.role,
              isDemo: user.is_demo,
            }))
            .filter((user) => StringUtils.isSubstring(query, user.name)),
          'name',
        );
      },

      render: suggestionsRenderer,
    },
  });
};

type ExtractReturnType<T> = T extends () => infer R ? R : never;

/**
 * `config` is what distinguishes the two consumers: the page-mention
 * typeahead passes one and gets `MentionSuggestions` (which fetches), the
 * user-mention typeahead passes none and gets the plain `SuggestionsList`.
 * It carried a third field, `feature`, that `MentionSuggestions` never read.
 */
interface SuggestionRenderConfig {
  pageType: number;
  pageId?: number;
}

type SuggestionRender = (
  config?: SuggestionRenderConfig,
) => ExtractReturnType<SuggestionOptions['render']>;

const suggestionsRenderer: SuggestionRender = (
  config?: SuggestionRenderConfig,
) => {
  let reactRenderer: (ReactRenderer & { ref: any }) | undefined;
  let popup: Instance[] | undefined;

  return {
    onStart: (props) => {
      reactRenderer = new ReactRenderer(
        // @ts-ignore
        config ? MentionSuggestions : SuggestionsList,
        {
          props: { ...props, config },
          editor: props.editor,
        },
      );

      if (!props.clientRect) {
        return;
      }

      popup = tippy('body', {
        getReferenceClientRect: props.clientRect as GetReferenceClientRect,
        appendTo: () => document.body,
        content: reactRenderer.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        maxWidth: '500px',
        arrow: false,
        theme: 'suggestion-list',
        placement: 'auto',
      });
    },
    onUpdate(props) {
      reactRenderer?.updateProps(props);

      if (!props.clientRect) {
        return;
      }

      popup?.forEach((tippy) => {
        tippy.setProps({
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
        });
      });
    },
    onKeyDown(props) {
      if (props.event.key === 'Escape') {
        popup?.forEach((tippy) => tippy.hide());
        props.event.stopPropagation();

        return true;
      }

      return reactRenderer?.ref?.onKeyDown(props);
    },

    onExit() {
      popup?.forEach((tippy) => tippy.destroy());
      reactRenderer?.destroy();
    },
  };
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fileAttachments: {
      // @override
      clearContent: () => ReturnType;
    };
  }
}

export type FileAttachmentsExtensionStorage = {
  getFiles: () => FileAttachment[];
  clearFiles: () => void;
  setFiles: (attachments: FileAttachment[]) => void;
};

const FileAttachmentsExtensions = () => {
  return Extension.create<{}, FileAttachmentsExtensionStorage>({
    name: TextEditorFeature.FileAttachments,
    addStorage() {
      return {
        setFiles: () => {},
        getFiles: () => [],
        clearFiles: () => {},
      };
    },
    addCommands() {
      return {
        // @override
        clearContent:
          () =>
          ({ chain }) => {
            this.storage.clearFiles();
            chain().setContent('').run();

            return false;
          },
      };
    },
  });
};
type AiContentExtensionStorage = {
  lastSelectionIsEmpty: boolean;
  lastSelectionText: string;
  lastSelection: undefined | Selection;
};

const AIContentExtension = ({
  setRefineTextSelectionVisible,
}: {
  setRefineTextSelectionVisible(value: boolean): void;
}) => {
  return Extension.create<{}, AiContentExtensionStorage>({
    name: TextEditorFeature.AIContent,
    addStorage() {
      return {
        lastSelectionIsEmpty: true,
        lastSelectionText: '',
        lastSelection: undefined,
      };
    },
    onUpdate() {
      const hasContent = this.editor.state.doc.textContent.trim().length > 0;

      setRefineTextSelectionVisible(hasContent);
    },
    onSelectionUpdate() {
      const { editor } = this;

      const {
        empty: lastSelectionIsEmpty,
        from: selectionFrom,
        to: selectionTo,
      } = editor.state.selection;

      const newlastSelectionText = editor.state.doc.textBetween(
        selectionFrom,
        selectionTo,
        ' ',
      );

      editor.extensionStorage.aiContent.lastSelection = editor.state.selection;
      editor.extensionStorage.aiContent.lastSelectionText =
        newlastSelectionText;
      editor.extensionStorage.aiContent.lastSelectionIsEmpty =
        lastSelectionIsEmpty;
    },
  });
};

const ClickableLinksExtension = Extension.create({
  name: 'clickableLinks',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('clickableLinks'),
        props: {
          handleClick(_view, _pos, event) {
            const anchor = (event.target as HTMLElement).closest('a');

            if (anchor?.href) {
              window.open(anchor.href, '_blank', 'noopener,noreferrer');
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});

const MentionCleanupExtension = Extension.create({
  name: 'mentionCleanup',

  addProseMirrorPlugins() {
    const handleCopy = (view: EditorView, event: ClipboardEvent) => {
      const { selection, doc } = view.state;
      const selectedText = doc.textBetween(
        selection.from,
        selection.to,
        '\n',
        (node: Node) => {
          if (node.type.name === 'hardBreak') return '\n';
          if (node.type.spec.leafText) return node.type.spec.leafText(node);
          return '';
        },
      );

      if (selectedText && event.clipboardData) {
        const cleanedText = stripMentionMarkup(selectedText);
        event.clipboardData.setData('text/plain', cleanedText);
        event.preventDefault();
        return true;
      }

      return false;
    };

    const handlePaste = (view: EditorView, event: ClipboardEvent) => {
      const text = event.clipboardData?.getData('text/plain');
      if (text) {
        const cleanedText = stripMentionMarkup(text);
        if (cleanedText !== text) {
          event.preventDefault();
          const { schema, tr } = view.state;
          const lines = cleanedText.split(/(?:\r\n?|\n)/);
          const nodes: Node[] = [];

          lines.forEach((line, index) => {
            if (index > 0 && schema.nodes.hardBreak) {
              nodes.push(schema.nodes.hardBreak.create());
            }
            if (line.length > 0) {
              nodes.push(schema.text(line));
            }
          });

          if (nodes.length > 0) {
            const fragment = Fragment.from(nodes);
            const slice = new Slice(fragment, 0, 0);
            tr.replaceSelection(slice);
            view.dispatch(tr);
          }
          return true;
        }
      }

      return false;
    };

    return [
      new Plugin({
        key: new PluginKey('mentionCleanup'),
        props: {
          handleDOMEvents: {
            copy: handleCopy,
            paste: handlePaste,
          },
        },
      }),
    ];
  },
});

export const PlainTextSeparatedWithHardBreaks = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.setHardBreak(),
    };
  },

  addProseMirrorPlugins() {
    function createNodesFromText(text: string, schema: any): Node[] {
      const blocks = text.split(/(?:\r\n?|\n)/);
      const nodes: Node[] = [];

      blocks.forEach((line, index) => {
        if (index > 0) {
          nodes.push(schema.nodes.hardBreak.create());
        }
        if (line.length > 0) {
          nodes.push(schema.text(line));
        }
      });

      return nodes;
    }

    const handlePaste = (view: EditorView, event: ClipboardEvent) => {
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain');
      if (text) {
        const { schema, tr } = view.state;
        const nodes = createNodesFromText(text, schema);
        const fragment = Fragment.from(nodes);
        const slice = new Slice(fragment, 0, 0);
        tr.replaceSelection(slice);
        view.dispatch(tr);
      }
      return true;
    };

    const plainTextSeparatedWithHardBreaks = new Plugin({
      key: new PluginKey('plainTextSeparatedWithHardBreaks'),
      props: {
        handlePaste,
        handleDOMEvents: {
          paste: handlePaste,
        },
      },
    });

    return [plainTextSeparatedWithHardBreaks];
  },
});

export type SubmitOnEnterStorage = {
  suggestionListOpen: boolean;
  suggestionHandledEnter: boolean;
};
